import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  ExitToApp as LogoutIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { signingApi } from '../services/api';
import { FormSummary, FormApplication, FormStatus } from 'form-application-shared';

const AdminPage: React.FC = () => {
  const [forms, setForms] = useState<FormSummary[]>([]);
  const [filteredForms, setFilteredForms] = useState<FormSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedForm, setSelectedForm] = useState<FormApplication | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState<FormSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sendingSigningLink, setSendingSigningLink] = useState<string | null>(null);
  const [envelopeIdDialogOpen, setEnvelopeIdDialogOpen] = useState(false);
  const [formToSetEnvelopeId, setFormToSetEnvelopeId] = useState<FormSummary | null>(null);
  const [envelopeIdInput, setEnvelopeIdInput] = useState('');
  const [settingEnvelopeId, setSettingEnvelopeId] = useState(false);
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [formToRevert, setFormToRevert] = useState<FormSummary | null>(null);
  const [reverting, setReverting] = useState(false);

  // Helper function to check if a form matches the search criteria
  const matchesSearchTerm = (form: FormSummary, searchTerm: string): boolean => {
    if (!searchTerm) return true;
    
    // Split search term into individual words and filter out empty strings
    const searchWords = searchTerm.toLowerCase().split(/\s+/).filter(word => word.length > 0);
    
    // Combine all searchable text from the form
    const searchableText = [
      form.email || '',
      form.salesforceName || '',
      form.salesforceOwnerEmail || '',
      form.id || '',
      form.status || ''
    ].join(' ').toLowerCase();
    
    // Check if all search words are found in the combined text
    return searchWords.every(word => searchableText.includes(word));
  };

  const getAuthToken = (): string | null => {
    return localStorage.getItem('adminToken');
  };

  const isAuthenticated = (): boolean => {
    return !!getAuthToken();
  };

  const handleLogin = () => {
    window.location.href = '/api/auth/login';
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    window.location.reload();
  };

  const fetchForms = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAuthToken();
      if (!token) {
        handleLogin();
        return;
      }

      const response = await fetch('/api/forms/admin/list', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        handleLogin();
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch forms: ${response.statusText}`);
      }

      const formSummaries: FormSummary[] = await response.json();
      setForms(formSummaries);
      setFilteredForms(formSummaries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch forms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check for auth token in URL (from callback)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const errorParam = urlParams.get('error');
    
    if (token) {
      localStorage.setItem('adminToken', token);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (errorParam) {
      let errorMessage = 'Authentication failed';
      if (errorParam === 'auth_failed') {
        errorMessage = 'Authentication failed or user not authorized for admin access';
      } else if (errorParam === 'callback_failed') {
        errorMessage = 'Authentication callback failed. Please try again.';
      }
      setError(errorMessage);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
      return;
    }

    if (isAuthenticated()) {
      fetchForms();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Filter forms based on search term using word-based matching
    const filtered = forms.filter(form => matchesSearchTerm(form, searchTerm));
    setFilteredForms(filtered);
  }, [searchTerm, forms]);

  const getStatusColor = (status: FormStatus) => {
    switch (status) {
      case FormStatus.COMPLETED:
        return 'success';
      case FormStatus.IN_PROGRESS:
        return 'warning';
      case FormStatus.INITIATED:
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (date: string | Date) => {
    return format(typeof date === 'string' ? new Date(date) : date, 'MMM dd, yyyy HH:mm');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatAddress = (addressLines: string, city: string, state: string, zip: string) => {
    const parts = [addressLines, city, state, zip].filter(Boolean);
    return parts.join(', ');
  };

  const renderFormSection = (title: string, children: React.ReactNode) => (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ width: '100%' }}>
          {children}
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderInfoGrid = (items: Array<{ label: string; value: any }>) => (
    <Grid container spacing={2}>
      {items.map((item, index) => (
        <Grid item xs={12} sm={6} key={index}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {item.label}
            </Typography>
            <Typography variant="body2">
              {item.value || 'Not provided'}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  const handleViewDetails = async (form: FormSummary) => {
    try {
      setLoadingDetails(true);
      setError(null);

      const token = getAuthToken();
      if (!token) {
        handleLogin();
        return;
      }

      const response = await fetch(`/api/forms/admin/${form.id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        handleLogin();
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch form details: ${response.statusText}`);
      }

      const formDetails: FormApplication = await response.json();
      setSelectedForm(formDetails);
      setDetailsOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch form details');
    } finally {
      setLoadingDetails(false);
    }
  };


  const handleDeleteClick = (form: FormSummary) => {
    setFormToDelete(form);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!formToDelete) return;

    try {
      setDeleting(true);
      setError(null);

      const token = getAuthToken();
      if (!token) {
        handleLogin();
        return;
      }

      const response = await fetch(`/api/forms/admin/${formToDelete.id}/delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        handleLogin();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to delete form: ${response.statusText}`);
      }

      // Remove the form from the local state
      const updatedForms = forms.filter(form => form.id !== formToDelete.id);
      setForms(updatedForms);
      setFilteredForms(updatedForms.filter(form => matchesSearchTerm(form, searchTerm)));

      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setFormToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete form');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFormToDelete(null);
  };

  const handleSendSigningLink = async (form: FormSummary) => {
    try {
      setSendingSigningLink(form.id);
      setError(null);

      await signingApi.sendSigningLink(form.id);
      
      // Show success message (you could use a snackbar here)
      alert(`Signing link sent successfully to ${form.email}`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to send signing link';
      setError(errorMessage);
    } finally {
      setSendingSigningLink(null);
    }
  };

  const handleSetEnvelopeId = (form: FormSummary) => {
    setFormToSetEnvelopeId(form);
    setEnvelopeIdInput(form.docusignEnvelopeId || '');
    setEnvelopeIdDialogOpen(true);
  };

  const handleEnvelopeIdSubmit = async () => {
    if (!formToSetEnvelopeId || !envelopeIdInput.trim()) return;

    try {
      setSettingEnvelopeId(true);
      setError(null);

      await signingApi.setDocusignEnvelopeId(formToSetEnvelopeId.id, envelopeIdInput.trim());
      
      // Update the form in the local state
      const updatedForms = forms.map(form => 
        form.id === formToSetEnvelopeId.id 
          ? { ...form, docusignEnvelopeId: envelopeIdInput.trim() }
          : form
      );
      setForms(updatedForms);
      setFilteredForms(updatedForms.filter(form => matchesSearchTerm(form, searchTerm)));

      // Close dialog and reset state
      setEnvelopeIdDialogOpen(false);
      setFormToSetEnvelopeId(null);
      setEnvelopeIdInput('');
      
      alert(`DocuSign envelope ID ${formToSetEnvelopeId.docusignEnvelopeId ? 'updated' : 'set'} successfully`);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to set envelope ID';
      setError(errorMessage);
    } finally {
      setSettingEnvelopeId(false);
    }
  };

  const handleEnvelopeIdCancel = () => {
    setEnvelopeIdDialogOpen(false);
    setFormToSetEnvelopeId(null);
    setEnvelopeIdInput('');
  };

  const handleRevertClick = (form: FormSummary) => {
    setFormToRevert(form);
    setRevertDialogOpen(true);
  };

  const handleRevertConfirm = async () => {
    if (!formToRevert) return;

    try {
      setReverting(true);
      setError(null);

      await signingApi.revertToInProgress(formToRevert.id);
      
      // Update the form in the local state
      const updatedForms = forms.map(form => 
        form.id === formToRevert.id 
          ? { ...form, status: FormStatus.IN_PROGRESS, docusignEnvelopeId: undefined }
          : form
      );
      setForms(updatedForms);
      setFilteredForms(updatedForms.filter(form => matchesSearchTerm(form, searchTerm)));

      // Close dialog and reset state
      setRevertDialogOpen(false);
      setFormToRevert(null);
      
      alert('Form status reverted to in progress successfully');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to revert form status';
      setError(errorMessage);
    } finally {
      setReverting(false);
    }
  };

  const handleRevertCancel = () => {
    setRevertDialogOpen(false);
    setFormToRevert(null);
  };

  if (!isAuthenticated()) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Admin Access Required
          </Typography>
          <Typography variant="body1" paragraph>
            Please sign in with your Microsoft account to access the admin panel.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleLogin}
          >
            Sign In with Microsoft
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Form Applications Admin
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchForms}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
          >
            Sign Out
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by email, Salesforce name, owner email, or form ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6">
              {filteredForms.length} of {forms.length} forms
            </Typography>
          </Box>

          {/* Forms Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Form ID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell>Envelope ID</TableCell>
                  <TableCell>Salesforce</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredForms.map((form) => (
                  <TableRow key={form.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {form.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{form.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={form.status}
                        color={getStatusColor(form.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(form.createdAt)}</TableCell>
                    <TableCell>{formatDate(form.updatedAt)}</TableCell>
                    <TableCell>
                      {form.docusignEnvelopeId ? (
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                          {form.docusignEnvelopeId}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Not set
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {form.salesforceName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {form.salesforceOwnerEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(form)}
                            disabled={loadingDetails}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {form.status === FormStatus.COMPLETED && form.docusignEnvelopeId && (
                          <Tooltip title="Send Signing Link">
                            <IconButton
                              size="small"
                              onClick={() => handleSendSigningLink(form)}
                              disabled={sendingSigningLink === form.id}
                              color="primary"
                            >
                              <SendIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {form.status === FormStatus.COMPLETED && (
                          <Tooltip title={form.docusignEnvelopeId ? "Edit Envelope ID" : "Set Envelope ID"}>
                            <IconButton
                              size="small"
                              onClick={() => handleSetEnvelopeId(form)}
                              color={form.docusignEnvelopeId ? "secondary" : "warning"}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {form.status === FormStatus.COMPLETED && (
                          <Tooltip title="Revert to In Progress">
                            <IconButton
                              size="small"
                              onClick={() => handleRevertClick(form)}
                              color="warning"
                            >
                              <UndoIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete Form">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(form)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredForms.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No forms found matching your search criteria.
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Form Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Form Application Details</Typography>
            {selectedForm && (
              <Chip
                label={selectedForm.status}
                color={getStatusColor(selectedForm.status) as any}
                size="medium"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedForm && (
            <Box sx={{ pt: 1 }}>
              {/* Basic Information */}
              {renderFormSection('Basic Information', 
                renderInfoGrid([
                  { label: 'Form ID', value: selectedForm.id },
                  { label: 'Email', value: selectedForm.email },
                  { label: 'Salesforce Name', value: selectedForm.salesforceName },
                  { label: 'Salesforce Owner', value: selectedForm.salesforceOwnerEmail },
                  { label: 'Created', value: formatDate(selectedForm.createdAt) },
                  { label: 'Last Updated', value: formatDate(selectedForm.updatedAt) },
                  { label: 'Completed', value: selectedForm.completedAt ? formatDate(selectedForm.completedAt) : 'Not completed' },
                  { label: 'Document Signing URL', value: selectedForm.documentSigningUrl ? 'Available' : 'Not generated' }
                ])
              )}

              {/* Signing Information */}
              {selectedForm.data && renderFormSection('Signing Information',
                renderInfoGrid([
                  { label: 'Signed By Name', value: selectedForm.data.signedByName },
                  { label: 'Signed By Email', value: selectedForm.data.signedByEmail }
                ])
              )}

              {/* Reinsurance Information */}
              {selectedForm.data && renderFormSection('Reinsurance Information',
                <Box>
                  {renderInfoGrid([
                    { label: 'Has Reinsurance', value: selectedForm.data.hasReinsurance ? 'Yes' : 'No' },
                    { label: 'No Finance Company Acknowledgment', value: selectedForm.data.noFinanceCompanyAck ? 'Yes' : 'No' }
                  ])}
                  {selectedForm.data.hasReinsurance && (
                    <Box sx={{ mt: 2 }}>
                      {renderInfoGrid([
                        { label: 'Reinsurance Name 1', value: selectedForm.data.reinsuranceName1 },
                        { label: 'Reinsurance Name 2', value: selectedForm.data.reinsuranceName2 },
                        { label: 'Reinsurance Name 3', value: selectedForm.data.reinsuranceName3 },
                        { label: 'Formation Fee', value: selectedForm.data.reinsuranceFormationFee ? formatCurrency(selectedForm.data.reinsuranceFormationFee) : 'Not set' },
                        { label: 'Management Fee', value: selectedForm.data.reinsuranceManagementFee ? formatCurrency(selectedForm.data.reinsuranceManagementFee) : 'Not set' }
                      ])}
                    </Box>
                  )}
                </Box>
              )}

              {/* Products */}
              {selectedForm.data?.products && selectedForm.data.products.length > 0 && renderFormSection('Products',
                <List dense>
                  {selectedForm.data.products.map((product: string, index: number) => (
                    <ListItem key={index}>
                      <ListItemText primary={product} />
                    </ListItem>
                  ))}
                </List>
              )}

              {/* Reinsurance Contact */}
              {selectedForm.data && selectedForm.data.reinsuranceContactName && renderFormSection('Reinsurance Contact',
                <Box>
                  {renderInfoGrid([
                    { label: 'Name', value: selectedForm.data.reinsuranceContactName },
                    { label: 'Relation', value: selectedForm.data.reinsuranceContactRelation },
                    { label: 'Email', value: selectedForm.data.reinsuranceContactEmail },
                    { label: 'Phone', value: selectedForm.data.reinsuranceContactPhone },
                    { label: 'Accounting Email', value: selectedForm.data.reinsuranceContactAccountingEmail }
                  ])}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Physical Address
                    </Typography>
                    <Typography variant="body2">
                      {formatAddress(
                        selectedForm.data.reinsuranceContactPhysicalAddressLines,
                        selectedForm.data.reinsuranceContactPhysicalCity,
                        selectedForm.data.reinsuranceContactPhysicalState,
                        selectedForm.data.reinsuranceContactPhysicalZip
                      )}
                    </Typography>
                  </Box>
                  {selectedForm.data.reinsuranceContactMailingEnabled && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Mailing Address
                      </Typography>
                      <Typography variant="body2">
                        {formatAddress(
                          selectedForm.data.reinsuranceContactMailingAddressLines,
                          selectedForm.data.reinsuranceContactMailingCity,
                          selectedForm.data.reinsuranceContactMailingState,
                          selectedForm.data.reinsuranceContactMailingZip
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Producers */}
              {selectedForm.data?.producers && selectedForm.data.producers.length > 0 && renderFormSection('Producers',
                <Box>
                  {selectedForm.data.producers.map((producer: any, index: number) => (
                    <Card key={index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Producer {index + 1}: {producer.legalName}
                        </Typography>
                        {renderInfoGrid([
                          { label: 'Legal Name', value: producer.legalName },
                          { label: 'Relationship', value: producer.relationship },
                          { label: 'Contact', value: producer.contact },
                          { label: 'Role', value: producer.role },
                          { label: 'Email', value: producer.email },
                          { label: 'Telephone', value: producer.telephone },
                          { label: 'Fax', value: producer.fax },
                          { label: 'Tax ID', value: producer.taxId },
                          { label: 'Tax Type', value: producer.taxType }
                        ])}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Address
                          </Typography>
                          <Typography variant="body2">
                            {formatAddress(producer.addressLines, producer.city, producer.state, producer.zip)}
                          </Typography>
                        </Box>
                        {producer.owners && producer.owners.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Owners
                            </Typography>
                            {producer.owners.map((owner: any, ownerIndex: number) => (
                              <Box key={ownerIndex} sx={{ ml: 2, mb: 1 }}>
                                <Typography variant="body2">
                                  <strong>{owner.legalName}</strong> - {owner.percentOwned}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {owner.email} | {formatAddress(owner.addressLines, owner.city, owner.state, owner.zip)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {/* People/Shareholders */}
              {selectedForm.data?.people && selectedForm.data.people.length > 0 && renderFormSection('People & Shareholders',
                <Box>
                  {selectedForm.data.people.map((person: any, index: number) => (
                    <Card key={index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {person.legalName}
                        </Typography>
                        {renderInfoGrid([
                          { label: 'Email', value: person.email },
                          { label: 'Tax ID', value: person.taxId },
                          { label: 'Date of Birth', value: person.dob },
                          { label: 'Percent Owned', value: person.percentOwned ? `${person.percentOwned}%` : 'N/A' },
                          { label: 'Is Shareholder', value: person.isShareholder ? 'Yes' : 'No' },
                          { label: 'Is Director', value: person.isDirector ? 'Yes' : 'No' },
                          { label: 'Relationship', value: person.relationship },
                          { label: 'Officer Types', value: person.officerTypes?.join(', ') || 'None' }
                        ])}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Address
                          </Typography>
                          <Typography variant="body2">
                            {formatAddress(person.addressLines, person.city, person.state, person.zip)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {/* ACH Payment Information */}
              {selectedForm.data?.ach && renderFormSection('ACH Payment Information',
                <Box>
                  {renderInfoGrid([
                    { label: 'Entity Name', value: selectedForm.data.ach.entityName },
                    { label: 'Bank Name', value: selectedForm.data.ach.bankName },
                    { label: 'Routing Number', value: selectedForm.data.ach.routingNumber },
                    { label: 'Account Number', value: selectedForm.data.ach.accountNumber ? '****' + selectedForm.data.ach.accountNumber.slice(-4) : 'Not provided' },
                    { label: 'Opted In', value: selectedForm.data.ach.optIn ? 'Yes' : 'No' },
                    { label: 'Acknowledged Terms', value: selectedForm.data.ach.acknowledgedTerms ? 'Yes' : 'No' },
                    { label: 'Voided Check File', value: selectedForm.data.ach.voidedCheckFileName || 'Not uploaded' }
                  ])}
                </Box>
              )}

              {/* ID Uploads */}
              {selectedForm.data?.idUploads && Object.keys(selectedForm.data.idUploads).length > 0 && renderFormSection('ID Uploads',
                <List>
                  {Object.entries(selectedForm.data.idUploads).map(([key, upload]: [string, any]) => (
                    <ListItem key={key}>
                      <ListItemText
                        primary={upload.name || key}
                        secondary={`Type: ${upload.type || 'Unknown'} | Uploaded: ${upload.uploadDate ? formatDate(upload.uploadDate) : 'Unknown'}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {/* Other Ownership Details */}
              {selectedForm.data?.otherOwnershipDetails && renderFormSection('Other Ownership Details',
                <Typography variant="body2">
                  {selectedForm.data.otherOwnershipDetails}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {formToDelete && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this form? This action cannot be undone.
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2">Form Details:</Typography>
                <Typography variant="body2">
                  <strong>ID:</strong> {formToDelete.id}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {formToDelete.email || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {formToDelete.status}
                </Typography>
                <Typography variant="body2">
                  <strong>Salesforce:</strong> {formToDelete.salesforceName || 'N/A'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Form'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set Envelope ID Dialog */}
      <Dialog
        open={envelopeIdDialogOpen}
        onClose={handleEnvelopeIdCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {formToSetEnvelopeId?.docusignEnvelopeId ? "Edit DocuSign Envelope ID" : "Set DocuSign Envelope ID"}
        </DialogTitle>
        <DialogContent>
          {formToSetEnvelopeId && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" gutterBottom>
                {formToSetEnvelopeId.docusignEnvelopeId 
                  ? "Update the DocuSign envelope ID for this form."
                  : "Set the DocuSign envelope ID for this completed form to enable sending signing links."
                }
              </Typography>
              <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2">Form Details:</Typography>
                <Typography variant="body2">
                  <strong>ID:</strong> {formToSetEnvelopeId.id}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {formToSetEnvelopeId.email || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {formToSetEnvelopeId.status}
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="DocuSign Envelope ID"
                value={envelopeIdInput}
                onChange={(e) => setEnvelopeIdInput(e.target.value)}
                placeholder="Enter the DocuSign envelope ID"
                helperText="This should be the envelope ID from DocuSign (e.g., 12345678-1234-1234-1234-123456789012)"
                disabled={settingEnvelopeId}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEnvelopeIdCancel} disabled={settingEnvelopeId}>
            Cancel
          </Button>
          <Button 
            onClick={handleEnvelopeIdSubmit} 
            color="primary" 
            variant="contained"
            disabled={settingEnvelopeId || !envelopeIdInput.trim()}
          >
            {settingEnvelopeId 
              ? (formToSetEnvelopeId?.docusignEnvelopeId ? 'Updating...' : 'Setting...')
              : (formToSetEnvelopeId?.docusignEnvelopeId ? 'Update Envelope ID' : 'Set Envelope ID')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revert to In Progress Dialog */}
      <Dialog
        open={revertDialogOpen}
        onClose={handleRevertCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revert to In Progress</DialogTitle>
        <DialogContent>
          {formToRevert && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to revert this form back to "In Progress" status?
              </Typography>
              <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                This will clear the DocuSign envelope ID and allow the form to be edited and resubmitted.
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2">Form Details:</Typography>
                <Typography variant="body2">
                  <strong>ID:</strong> {formToRevert.id}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {formToRevert.email || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Salesforce:</strong> {formToRevert.salesforceName || 'N/A'}
                </Typography>
                {formToRevert.docusignEnvelopeId && (
                  <Typography variant="body2">
                    <strong>Envelope ID:</strong> {formToRevert.docusignEnvelopeId}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRevertCancel} disabled={reverting}>
            Cancel
          </Button>
          <Button 
            onClick={handleRevertConfirm} 
            color="warning" 
            variant="contained"
            disabled={reverting}
          >
            {reverting ? 'Reverting...' : 'Revert to In Progress'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPage;
